import Shepherd from 'shepherd.js';
import { TOUR_BUTTONS } from '../shepherd-defaults';
import {
  dismissCurrentShepherdStep,
  ensureShepherdModalOverlayHidden,
  exitShepherdLocationTypingFreeze,
  hideCurrentShepherdStepForTourTransition,
  registerInteractiveTourGuards,
  clearShepherdStepTooltipPersistenceSuppress,
  suppressShepherdStepTooltipPersistence,
  type ShepherdStep,
  wasShepherdStepDismissedByUser,
} from '../shepherd-interactive-step';
import {
  registerTourHighlightedFieldLockGuards,
  releaseAllShepherdTourFieldLocks,
  SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
  withTourFieldLockTipMessage,
} from '../shepherd-tour-field-lock';
import {
  resetTeeTimePollCoursesTargetPadding,
  resolveTeeTimePollCoursesTarget,
  setTeeTimePollCoursesInteractionActive,
} from '../tee-time-poll-courses.shepherd';
import { waitForElement } from '../shepherd.utils';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const TEE_TIME_POLL_TOUR = 'tee-time-poll';

/** Applied to `body` while the tee time poll Shepherd tour runs (stacking + blur). */
export const TEE_TIME_POLL_TOUR_BODY_CLASS = 'shepherd-tee-time-poll-tour-active';


export function setTeeTimePollTourBodyActive(active: boolean): void {
  document.body.classList.toggle(TEE_TIME_POLL_TOUR_BODY_CLASS, active);
}

export const TEE_TIME_POLL_STEP_COURSES = 'tee-time-poll-golf-courses';
export const TEE_TIME_POLL_STEP_DATES = 'tee-time-poll-dates';
export const TEE_TIME_POLL_STEP_TIMES = 'tee-time-poll-time-slots';

const MODAL_STEP_TO_TOUR_STEP: Record<number, string> = {
  2: TEE_TIME_POLL_STEP_COURSES,
  3: TEE_TIME_POLL_STEP_DATES,
  4: TEE_TIME_POLL_STEP_TIMES,
};

const SELECTORS = {
  courses: '[data-tour="tee-time-poll-golf-courses"]',
  dates: '[data-tour="tee-time-poll-dates"]',
  times: '[data-tour="tee-time-poll-time-slots"]',
} as const;

export function getTeeTimePollModalStep(): number {
  const raw = document
    .querySelector('.tee-time-poll-modal-content')
    ?.getAttribute('data-tee-time-poll-step');
  const step = raw ? Number(raw) : 0;
  return Number.isFinite(step) ? step : 0;
}

export function getTeeTimePollTourStepIdForModalStep(modalStep: number): string | undefined {
  return MODAL_STEP_TO_TOUR_STEP[modalStep];
}

function getTeeTimePollSelectorForStepId(stepId: string): string | undefined {
  if (stepId === TEE_TIME_POLL_STEP_COURSES) {
    return SELECTORS.courses;
  }
  if (stepId === TEE_TIME_POLL_STEP_DATES) {
    return SELECTORS.dates;
  }
  if (stepId === TEE_TIME_POLL_STEP_TIMES) {
    return SELECTORS.times;
  }
  return undefined;
}

function clearTeeTimePollTourUiForStep(stepId: string | undefined): void {
  if (stepId === TEE_TIME_POLL_STEP_COURSES) {
    exitShepherdLocationTypingFreeze(resolveTeeTimePollCoursesTarget);
    setTeeTimePollCoursesInteractionActive(false);
    resetTeeTimePollCoursesTargetPadding();
    releaseAllShepherdTourFieldLocks();
  }
}

/** Hide the open tip as soon as the poll wizard changes page (avoids flash while waiting for the next step). */
export function prepareTeeTimePollTourForModalStep(tour: ShepherdTour, modalStep: number): void {
  const nextStepId = getTeeTimePollTourStepIdForModalStep(modalStep);
  if (!nextStepId) {
    return;
  }
  const current = tour.getCurrentStep?.() as ShepherdStep | undefined;
  const currentId = current?.id;
  if (!currentId || currentId === nextStepId) {
    return;
  }
  clearTeeTimePollTourUiForStep(currentId);
  suppressShepherdStepTooltipPersistence(current);
  hideCurrentShepherdStepForTourTransition(tour, nextStepId);
}

/** After modal page is in the DOM, show the matching tour step (no fixed delay). */
export function showTeeTimePollTourStepWhenReady(tour: ShepherdTour, modalStep: number): void {
  const stepId = getTeeTimePollTourStepIdForModalStep(modalStep);
  if (!stepId || !isTeeTimePollModalOpen()) {
    return;
  }
  const targetStep = tour.getById?.(stepId) as ShepherdStep | undefined;
  if (wasShepherdStepDismissedByUser(targetStep)) {
    return;
  }
  const selector = getTeeTimePollSelectorForStepId(stepId);
  if (!selector) {
    return;
  }
  prepareTeeTimePollTourForModalStep(tour, modalStep);
  void waitForElement(selector, 8000).then((el) => {
    if (!el || !isTeeTimePollModalOpen() || getTeeTimePollModalStep() !== modalStep) {
      return;
    }
    if (wasShepherdStepDismissedByUser(targetStep)) {
      return;
    }
    void tour.show(stepId);
  });
}

function isTeeTimePollModalOpen(): boolean {
  const toggle = document.getElementById('toggleTeeTimePollModal') as HTMLInputElement | null;
  return !!toggle?.checked;
}

/** Close this step tip only; poll tour continues on other wizard steps. */
const teeTimePollCoursesOk = {
  text: 'OK',
  classes: 'shepherd-button-primary',
  action(this: ShepherdTour) {
    const root = resolveTeeTimePollCoursesTarget();
    const active = document.activeElement;
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement
    ) {
      if (root?.contains(active)) {
        active.blur();
      }
    }
    exitShepherdLocationTypingFreeze(resolveTeeTimePollCoursesTarget);
    setTeeTimePollCoursesInteractionActive(false);
    resetTeeTimePollCoursesTargetPadding();
    releaseAllShepherdTourFieldLocks();
    dismissCurrentShepherdStep(this);
    ensureShepherdModalOverlayHidden();
  },
};

/** Close this step tip only. */
const teeTimePollOkDismiss = {
  text: 'OK',
  classes: 'shepherd-button-primary',
  action(this: ShepherdTour) {
    dismissCurrentShepherdStep(this);
  },
};

/** Skip tour permanently (same as other tours) and clear poll overlay state. */
const teeTimePollSkipTour = {
  text: 'Skip Tour',
  classes: 'shepherd-button-secondary shepherd-button-skip',
  action(this: ShepherdTour) {
    clearTeeTimePollTourUiForStep(TEE_TIME_POLL_STEP_COURSES);
    ensureShepherdModalOverlayHidden();
    void this.cancel();
  },
};

/**
 * Create Tee Time Poll modal: golf courses → dates → time slots (one tip per wizard step).
 * Use modal Back/Next to change wizard steps; tour tips follow via `showTeeTimePollTourStepForModalStep`.
 */
export function buildTeeTimePollTour(tour: ShepherdTour): void {
  registerInteractiveTourGuards(tour);
  registerTourHighlightedFieldLockGuards(tour, [TEE_TIME_POLL_STEP_COURSES], resolveTeeTimePollCoursesTarget);

  tour.on('show', (ev: { step?: { id?: string } }) => {
    if (ev?.step?.id === TEE_TIME_POLL_STEP_COURSES) {
      setTeeTimePollCoursesInteractionActive(true);
      resetTeeTimePollCoursesTargetPadding();
      return;
    }
    if (ev?.step?.id) {
      setTeeTimePollCoursesInteractionActive(false);
      resetTeeTimePollCoursesTargetPadding();
      releaseAllShepherdTourFieldLocks();
    }
  });

  tour.on('hide', (ev: { step?: ShepherdStep }) => {
    clearShepherdStepTooltipPersistenceSuppress(ev?.step);
  });

  tour.addStep({
    id: TEE_TIME_POLL_STEP_COURSES,
    title: 'Tip',
    text: withTourFieldLockTipMessage(
      'Search for courses or pick from popular options so your group can vote on where to play.',
    ),
    attachTo: { element: SELECTORS.courses, on: 'top' },
    buttons: [teeTimePollSkipTour, teeTimePollCoursesOk],
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    showOn: () => isTeeTimePollModalOpen() && getTeeTimePollModalStep() === 2,
    beforeShowPromise() {
      return waitForElement(SELECTORS.courses, 8000).then(() => undefined);
    },
  });

  tour.addStep({
    id: TEE_TIME_POLL_STEP_DATES,
    title: 'Tip',
    text: 'Tap dates on the calendar to add days your group can choose from (optional).',
    attachTo: { element: SELECTORS.dates, on: 'top' },
    buttons: [teeTimePollSkipTour, teeTimePollOkDismiss],
    canClickTarget: true,
    scrollTo: false,
    floatingUIOptions: {
      strategy: 'fixed' as const,
    },
    showOn: () => isTeeTimePollModalOpen() && getTeeTimePollModalStep() === 3,
    beforeShowPromise() {
      return waitForElement(SELECTORS.dates, 8000).then(() => undefined);
    },
  });

  tour.addStep({
    id: TEE_TIME_POLL_STEP_TIMES,
    title: 'Tip',
    text: 'Select tee times that work for you. If you added multiple dates, use the arrows to set times for each day.',
    attachTo: { element: SELECTORS.times, on: 'top' },
    buttons: [teeTimePollSkipTour, TOUR_BUTTONS.finish],
    canClickTarget: true,
    scrollTo: false,
    floatingUIOptions: {
      strategy: 'fixed' as const,
    },
    showOn: () => isTeeTimePollModalOpen() && getTeeTimePollModalStep() === 4,
    beforeShowPromise() {
      return waitForElement(SELECTORS.times, 8000).then(() => undefined);
    },
  });
}
